// AdminEscalationsPage — /admin/escalations：**agent 说它做不了的那些事**
// （第一百一十批 B ④，需求方点名：「给管理员提供查看这些问题反馈的界面」）。
//
// ── 这一页回答的问题 ────────────────────────────────────────────────────────
// 不是「这张单怎么办」（那是审批面的事，它只看当前轮、未处置的那一条），而是
// **「这套系统交派任务的能力出了什么系统性问题」**。所以这里横切着看：按 reason
// 计数、跨工单、跨轮次、连已处置的也翻得出来。
//
// `byReason` 那一行是本页真正的产出。逃生口最大的产品风险是**它变成 agent 的默认
// 出路**——对一个被按调用数计费、被提示词要求"不要为了给结论而给结论"的模型来说，
// 说一句"我做不了"是最省力的一格。一旦它成为常态，审批面上出现的就不是预读而是一串
// 托辞，而人会学着忽略整块内容。四道防线里只有这一道是**可计量**的：
// `insufficient_context` 长期占多数 ⇒ 问题在**装配器**（我们喂给它的材料不够），
// 不在 agent；总占比超过预读总数的 15% ⇒ 先怀疑装配器，其次才怀疑提示词。
// 所以那个计数必须是**全集**（服务端不受 limit/offset 影响地给），页面绝不从当前页
// 的行里重算——一个随翻页而变的比例比没有比例更坏。
//
// ── 三条呈现纪律 ────────────────────────────────────────────────────────────
// ① `detail` 与 `toolTrace` **纯文本渲染，绝不 markdown**：它们可能复述 agent 读到的
//    仓内容（第三方写的材料）。这一页的读者是管理员，而管理员正是最不该在一个诊断
//    面上被渲染出来的链接骗走的人。
// ② 处置**单向**：没有"取消处置"。一个能收回的处置是一个可反复试探的开关，
//    而这张表的 ack 列与 direct_message.reported_at_ms 同一条纪律。
// ③ 处置**必须写说明**：一次没有说法的处置，与「没人管」在台账上无法区分。
//
// 判据（过滤 → 查询参数、归一化、计数排序、说明校验）全在 pages/adminEscalations.mjs
// 配单测；这里只渲染（SB-042：`.jsx` 里不放判据）。
import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Paper, Stack, TableBody, TableCell, TableHead, TableRow, TextField,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import AdminSubnav from '../components/AdminSubnav.jsx';
import AppSnackbar, { useNotify } from '../components/AppSnackbar.jsx';
import DataTable from '../components/DataTable.jsx';
import HelpLink from '../components/HelpLink.jsx';
import PagerBar from '../components/PagerBar.jsx';
import { fmtTime } from '../components/TicketDetailView.jsx';
import { useLoad } from '../hooks/useLoad.mjs';
import { useLang, useT } from '../i18n/index.mjs';
import {
  ACK_ERR_KEYS, ACK_NOTE_MAX, DEFAULT_FILTER, ESCALATION_FILTERS, ESC_PAGE_LIMIT,
  ackFormState, byReasonText, escalationPager, escalationQuery, isAcked, normalizeEscalations,
  reasonLabelKey,
} from './adminEscalations.mjs';

export default function AdminEscalationsPage() {
  const t = useT();
  const { lang } = useLang();
  const { snack, notify, clearSnack } = useNotify();
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  // SB-159（第三百八十二批）：翻页。此前从不发 offset，缺省「未处置」视图里第 51 条起静静消失。
  // 换过滤即回第一页；处置之后 reload 停在同一页，那一页被处置空了就退一页（escalationPager）。
  const [offset, setOffset] = useState(0);
  const changeFilter = (v) => { setFilter(v); setOffset(0); };

  const load = useLoad(
    () => api.adminAgentEscalations(escalationQuery(filter, { limit: ESC_PAGE_LIMIT, offset })),
    [filter, offset],
  );
  const rows = useMemo(() => normalizeEscalations(load.data?.escalations), [load.data]);
  // 复审（N4）：范围与下一页按服务端回显的 limit／offset 算（它会钳页长）；这里给的两个数只是回落值。
  const pager = load.status === 'ready' ? escalationPager(load.data, offset, ESC_PAGE_LIMIT) : null;
  useEffect(() => {
    if (pager?.stepBack) setOffset(pager.prevOffset);
  }, [pager?.stepBack, pager?.prevOffset]);

  return (
    <Box>
      <AdminSubnav />
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
        {t('admin.escalations.title')}
        <HelpLink section="admin-escalations" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('admin.escalations.desc')}
      </Typography>

      {load.status === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : load.status === 'error' ? (
        <Box>
          <Alert severity="error" sx={{ mb: 1 }}>
            {t(errKey(load.error, { fallback: 'common.error.network' }))}
          </Alert>
          <Button size="small" onClick={load.reload}>{t('common.retry')}</Button>
        </Box>
      ) : (
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} useFlexGap
                 sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={filter}
              onChange={(e, v) => { if (v) changeFilter(v); }}
            >
              {ESCALATION_FILTERS.map((f) => (
                <ToggleButton key={f} value={f}>{t(`admin.escalations.filter.${f}`)}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            {/* 全集计数。**不受上面那个过滤器影响**（服务端如此，页面也如此）：
                它回答的是"逃生口有没有变成默认出路"，而那个问题按未处置分片就答不了。 */}
            <Typography variant="body2" color="text.secondary">
              {t('admin.escalations.byReason', {
                list: byReasonText(load.data?.byReason,
                                   (r) => (reasonLabelKey(r) ? t(reasonLabelKey(r)) : r)) || '—',
              })}
            </Typography>
          </Stack>

          {/* SB-159：翻页器 + 未处置全集计数。total 缺席（旧后端）时**不许**读成「只有这一页」。 */}
          {pager?.unknown ? (
            <Alert severity="warning" data-paging-notice="admin.escalations.totalUnknown">
              {t('admin.escalations.totalUnknown', { n: rows.length })}
            </Alert>
          ) : pager && !pager.stepBack ? (
            <Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              {/* 复审（N7）：total 为 0 时不挂翻页器——空态已经说了「没有」，「第 0–0 条，共 0 条」只是噪音。 */}
              {pager.showBar ? (
                <PagerBar
                  text={t('admin.escalations.range', { from: pager.from, to: pager.to, total: pager.total })}
                  hasPrev={pager.hasPrev}
                  hasNext={pager.hasNext}
                  onPrev={() => setOffset(pager.prevOffset)}
                  onNext={() => setOffset(pager.nextOffset)}
                  prevLabel={t('pager.prev')}
                  nextLabel={t('pager.next')}
                />
              ) : null}
              <Typography variant="body2" color="text.secondary">
                {t('admin.escalations.unacked', { n: Number(load.data?.unacked) || 0 })}
              </Typography>
            </Stack>
          ) : null}

          {rows.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('admin.escalations.empty')}</Typography>
            </Paper>
          ) : (
            // detail 是自由文本（一到三句），toolTrace 每行一条路径——min-content
            // 早已超过平板宽，minWidth 接管（同 /admin/settings 那张表的口径）。
            <DataTable minWidth={900}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.escalations.col.at')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.escalations.col.op')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.escalations.col.entity')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.escalations.col.reason')}</TableCell>
                  <TableCell>{t('admin.escalations.col.detail')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.escalations.col.state')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <EscalationRow
                    key={row.id}
                    row={row}
                    t={t}
                    lang={lang}
                    notify={notify}
                    onAcked={load.reload}
                  />
                ))}
              </TableBody>
            </DataTable>
          )}
        </Stack>
      )}
      <AppSnackbar snack={snack} onClose={clearSnack} />
    </Box>
  );
}

// 一行 = 一条逃生口报告。
//
// 处置走**对话框**而不是就地展开的表单，理由是这个动作**单向不可撤销**（没有
// unack 列，也永远不该有）。本仓对不可逆动作的既有纪律就是"必须由一个说清了后果的
// 确认对话触发"（takeSecretDelivery 的"取走就没了"、reportMessage 的"交出去收不回"
// 都是同一形状）。就地表单在一张长表里还有第二个毛病：一个 200 行的表上同时敞着
// 二十个说明框，人很容易把说明写进相邻的那一行。
function EscalationRow({ row, t, lang, notify, onAcked }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const form = ackFormState({ note });
  const acked = isAcked(row);
  const reasonKey = reasonLabelKey(row.reason);

  const ack = async () => {
    setBusy(true);
    try {
      await api.adminAckAgentEscalation(row.id, form.payload.note);
      setOpen(false);
      setNote('');
      onAcked();
    } catch (e) {
      notify(t(errKey(e, { byCode: ACK_ERR_KEYS, fallback: 'common.error.network' })), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <TableRow>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTime(row.createdAtMs, lang)}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'ui-monospace, monospace' }}>
        {row.op}
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Stack spacing={0.25}>
          {/* 编号是**快照**：工单可能已被归档或撤回，而"我做不了 MNT-000012"在那张
              工单消失之后仍然是有效的改进线索。所以这里不做任何"点进去看"的链接
              ——一条指向已消失实体的链接比一段纯文本更让人以为系统坏了。 */}
          <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, monospace' }}>
            {row.entityNo || `${row.entityKind} #${row.entityId}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.projectGroup || '—'}
            {row.round > 0 ? ` · ${t('agentReview.round', { n: row.round })}` : ''}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {/* 值域外 ⇒ 不出标签（印一个裸键名给用户是更坏的一种"有信息"）。 */}
        {reasonKey
          ? <Chip size="small" variant="outlined" color="warning" label={t(reasonKey)} />
          : <Typography variant="caption" color="text.secondary">—</Typography>}
      </TableCell>
      <TableCell>
        {/* ⚠ 纯文本。它可能复述 agent 读到的仓内容——第三方材料，只作证据。 */}
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{row.detail || '—'}</Typography>
        {row.wouldNeed.length > 0 ? (
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary" component="div">
              {t('admin.escalations.wouldNeed')}
            </Typography>
            <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2.5 }}>
              {row.wouldNeed.map((s, i) => (
                <Typography key={i} component="li" variant="body2">{s}</Typography>
              ))}
            </Stack>
          </Box>
        ) : null}
        {row.directives.length > 0 ? (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            {/* 一条「带着 draft_work_item 跑，然后说做不了」与「什么都没带就说做不了」
                是两个不同的结论——所以本轮带了什么命令必须列出来。 */}
            {t('admin.escalations.directives')} {row.directives.join(' · ')}
          </Typography>
        ) : null}
        {row.toolTrace.length > 0 ? (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            {/* **它放弃之前查过什么**，正是"上下文不足"这句话的证据。
                没有它，逃生口就是一句无法核对的抱怨。 */}
            {t('admin.escalations.toolTrace', { n: row.toolTrace.length })}
            {row.toolTrace.map((c, i) => (
              <Box component="span" key={i} sx={{ display: 'block', pl: 1.5 }}>
                {c.ok ? '✓' : '✗'} {c.note || c.tool}
              </Box>
            ))}
          </Typography>
        ) : null}
        {row.model ? (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            {row.model}{row.jobId > 0 ? ` · job #${row.jobId}` : ''}
          </Typography>
        ) : null}
      </TableCell>
      <TableCell sx={{ minWidth: 220 }}>
        {acked ? (
          <Stack spacing={0.25}>
            <Typography variant="caption" color="text.secondary">
              {t('admin.escalations.acked', {
                who: row.ackBy == null ? '—' : String(row.ackBy),
                when: fmtTime(row.ackAtMs, lang),
              })}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{row.ackNote || '—'}</Typography>
          </Stack>
        ) : (
          <>
            <Button size="small" variant="outlined" onClick={() => setOpen(true)}>
              {t('admin.escalations.ack')}
            </Button>
            <Dialog open={open} onClose={busy ? undefined : () => setOpen(false)}
                    fullWidth maxWidth="sm">
              <DialogTitle>
                {t('admin.escalations.ack')} · {row.entityNo || `#${row.entityId}`}
              </DialogTitle>
              <DialogContent>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  autoFocus
                  /* helperText 而不是 placeholder：它要求的是一段**有说法**的话，
                     而 placeholder 在人开始打字的一瞬间就消失了——恰好是他还在想
                     "该写什么"的那一刻。这一句同时是那条闸的解释。 */
                  helperText={t('admin.escalations.ackNotePlaceholder')}
                  value={note}
                  disabled={busy}
                  error={form.chars > ACK_NOTE_MAX}
                  onChange={(e) => setNote(e.target.value)}
                  sx={{ mt: 1 }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpen(false)} disabled={busy}>{t('common.cancel')}</Button>
                {/* 空说明（含全是空白的）按不动——服务端同样 400，这里是同一条判据的
                    第二份，为的是不让人打完一段话才被拒。 */}
                <Button variant="contained" disabled={busy || !form.canSubmit} onClick={ack}>
                  {t('admin.escalations.ack')}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </TableCell>
    </TableRow>
  );
}
